import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getToken, setToken, clearStorage } from '../libraries/amplify.library';
import { fetchAuthSession } from 'aws-amplify/auth';

export class BaseService {
  public readonly instance: AxiosInstance;

  public constructor(baseURL: string) {
    this.instance = axios.create({ 
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      }
    });
    this.initRequestInterceptor();
    this.initResponseInterceptor();
  }

  private initRequestInterceptor = () => {
    this.instance.interceptors.request.use(
      this.handleRequest,
      this.handleError
    );
  };

  private handleRequest = async (config: any) => {
    // Add auth token to requests if available
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };



  private initResponseInterceptor = () => {
    this.instance.interceptors.response.use(
      this.handleResponse,
      this.handleError
    );
  };

  private handleResponse = (data: AxiosResponse) => {
    return data;
  };

  protected handleError = async (error: any) => {
    if (error.response) {
      const { status, data, config } = error.response;
      
      // Handle 401 Unauthorized - attempt token refresh and retry once
      if (status === 401 && !config._retry) {
        //console.log('401 Unauthorized - attempting token refresh');
        config._retry = true;
        
        try {
          const session = await fetchAuthSession({ forceRefresh: true });
          
          if (session && session.tokens && session.tokens.accessToken) {
            const newToken = session.tokens.accessToken.toString();
            setToken(newToken);
            config.headers.Authorization = `Bearer ${newToken}`;
            //console.log('Token refreshed, retrying request');
            return this.instance(config);
          } else {
            console.error('Token refresh failed - no valid tokens');
            clearStorage();
            window.location.href = '/login';
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          clearStorage();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
      
      // Handle AWS WAF CAPTCHA challenge (status 405)
      if (status === 405 && data) {
        // Check if response contains CAPTCHA challenge HTML
        if (typeof data === 'string' && data.includes('captcha')) {
          console.error('AWS WAF CAPTCHA challenge detected. Ensure CloudFront is configured to handle CAPTCHA.');
          // Return a more user-friendly error
          return Promise.reject({
            ...error,
            message: 'CAPTCHA verification required. Please refresh and try again.',
            isCaptchaChallenge: true
          });
        }
      }
      
      //console.log(`Error: status: ${status}, data: ${data}, config:${config}`);
    } else {
      console.log(error);
    }
    return Promise.reject(error);
  };
}

export const getServiceInstance = () => {
  const service = new BaseService('');
  return service.instance;
};
